import { doWrapInList } from '@common/prosemirror/commands/list.prosemirror';
import { setBlock } from '@common/prosemirror/commands/set-block-type.command';
import { getTrackedChangesState, TrackedChangesTrackedMetaKey } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { copyNodeWithChanges, copySliceWithChanges } from '@common/prosemirror/tracked-changes/copy';
import { Node, NodeRange, NodeType, ProseMirrorNode, Slice } from 'prosemirror-model';
import { Command, EditorState, Selection, TextSelection } from 'prosemirror-state';
import { CellSelection } from 'prosemirror-tables';
import { findWrapping } from 'prosemirror-transform';

/**
 * wrapInList
 * builds a command that wraps a node in a list (eg ul/ol/dl).
 * The block nodes being wrapped are converted into itemChildType if possible.
 * @param listType the list type to wrap
 * @param itemChildType the type to convert block nodes into
 * @param attrsBehavior options for changing block nodes to the child type, and keeping or removing the attributes
 * @returns the command for wrapping a node in a list
 */
export function wrapInList(listType: NodeType, itemChildType: NodeType, attrsBehavior: 'keep' | 'move-to-item' | 'remove'): Command {
  return function (state: EditorState, dispatch?: ProsemirrorDispatcher): boolean {
    // Wrapping a cell selection in a list is not supported
    if (state.selection instanceof CellSelection) {
      return false;
    }

    const { $from, $to } = state.selection;

    let range = $from.blockRange($to);
    if (!range) {
      return false;
    }

    let doJoin = false;
    let outerRange = range;

    // This is at the top of an existing list item
    if (range.depth >= 2 && $from.node(range.depth - 1).type.compatibleContent(listType) && range.startIndex === 0) {
      // Don't do anything if this is the top of the list
      if ($from.index(range.depth - 1) === 0) {
        return false;
      }

      const $insert = state.doc.resolve(range.start - 2);
      outerRange = new NodeRange($insert, $insert, range.depth);

      if (range.endIndex < range.parent.childCount) {
        range = new NodeRange($from, state.doc.resolve($to.end(range.depth)), range.depth);
      }

      doJoin = true;
    }

    const wrap = findWrapping(outerRange, listType, null, range);
    if (!wrap) {
      return false;
    }

    if (dispatch) {
      const tr = state.tr;
      const attrs: Dictionary[] = [];

      // Change the block nodes that are being wrapped by the list into the item child type
      if (attrsBehavior === 'keep') {
        let childPos = range.start;
        for (let i = range.startIndex; i < range.endIndex; i += 1) {
          const child = range.parent.child(i);
          /* Copy the attrs over to the new block.
          * isNodeSelection is set to false because this feature is not tracked  with tracked changes,
          * when that changes check if the selection is a node selection for the third parameter.
          */
          setBlock(tr, childPos, childPos + child.nodeSize, false, itemChildType, null);
          childPos += child.nodeSize;
        }
      } else if (attrsBehavior === 'move-to-item') {
        // Store the attrs to be copied over to the list items after the content is wrapped in the list
        for (let i = range.startIndex; i < range.endIndex; i += 1) {
          const child = range.parent.child(i);
          attrs.push(child.attrs);
        }
        // Do not copy the attrs to the new block because they are going to be moved to the item instead
        tr.setBlockType(range.start, range.end, itemChildType);
      } else if (attrsBehavior === 'remove') {
        // Remove the attrs when changing the block type
        tr.setBlockType(range.start, range.end, itemChildType);
      }

      // Wrap the block nodes in the list
      doWrapInList(tr, range, wrap, doJoin, listType);

      // Move the attrs to the list items
      if (attrsBehavior === 'move-to-item') {
        const listNode = tr.doc.nodeAt(outerRange.start);

        let childPos = outerRange.start + 1;
        for (let i = 0; i < listNode.childCount; i += 1) {
          const child = listNode.child(i);
          tr.setNodeMarkup(childPos, null, attrs[i]);
          childPos += child.nodeSize;
        }
      }

      const trackedChangesState = getTrackedChangesState(state);
      if (trackedChangesState?.enabled) {
        // Replace the new list with a tracked add copy
        const list: Node = tr.doc.nodeAt(range.start);
        const trackedAddList: ProseMirrorNode = copyNodeWithChanges(trackedChangesState.changeMarkType, list, trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Add));

        tr.replaceWith(range.start, tr.mapping.map(range.end), trackedAddList);

        // Set selection as the same in the list
        const selAtStartOfList: Selection = Selection.atStart(list);
        const selAtEndOfList: Selection = Selection.atEnd(list);
        // Position before list plus offset to start of list plus the offset to the first and last selectable position
        tr.setSelection(new TextSelection(tr.doc.resolve(range.start + 1 + selAtStartOfList.from), tr.doc.resolve(range.start + 1 + selAtEndOfList.from)));

        // Insert a tracked delete from the old selection
        const deletedNodes: Slice = copySliceWithChanges(trackedChangesState?.changeMarkType, state.doc.slice(range.start, range.end), trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Remove));

        tr.insert(range.start, deletedNodes.content);

        // Mark that the changes were tracked
        tr.setMeta(TrackedChangesTrackedMetaKey, true);
      }

      dispatch(tr.scrollIntoView());
    }

    return true;
  };
}
